import qs from 'qs';
import moment from 'moment';
import api from './api';
import AxiosUtil from '../utils/AxiosUtil';
import generateSearchRequest from '../utils/sequelize-search-builder-client';

const selectOrder = (order) => {
	return {
		...order,
		dispatchDate: order.dispatchDate ? moment(order.dispatchDate).toDate() : null,
		deliveryDate: order.deliveryDate ? moment(order.deliveryDate).toDate() : null,
		createdAt: order.createdAt ? moment(order.createdAt).toDate() : null,
		updatedAt: order.updatedAt ? moment(order.updatedAt).toDate() : null,
		deletedAt: order.deletedAt ? moment(order.deletedAt).toDate() : null,
	};
};

const selectOrders = (orders) => orders.map(selectOrder);

class OrderBtbService {
	#prefix;

	constructor() {
		this.#prefix = '/orders-btb';
	}

	async getAll({ page = 0, size = 10, order, filters }) {
		const settings = {
			where: {
				id: {
					as: 'id',
					type: 'like',
				},
				companyName: {
					as: 'company.name',
					type: 'like',
				},
				trackingCode: {
					as: 'trackingCode',
					type: 'like',
				},
				delivermanName: {
					as: 'deliverman.name',
					type: 'like',
				},
				dispatchDate: {
					as: 'dispatchDate',
					type: 'equal',
				},
				baseName: {
					as: 'base.name',
					type: 'like',
				},
				baseId: {
					as: 'baseId',
					type: 'equal',
				},
				status: {
					as: 'status',
					type: 'like',
				},
				'createdAt:between': {
					as: 'createdAt',
					type: 'between',
				},
			},
			order,
		};
		const request = generateSearchRequest(filters, settings);
		return api
			.get(this.#prefix, {
				paramsSerializer: {
					serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
				},
				params: { page, size, ...request },
			})
			.then((response) => response.data)
			.then((data) => ({ ...data, body: selectOrders(data.body) }))
			.catch(
				AxiosUtil.handleError.bind(
					AxiosUtil,
					'fail when trying to get ordersBtb in OrderBtbService.',
				),
			);
	}

	async downloadCsv() {
		return api
			.get(`${this.#prefix}/download`)
			.then((response) => response.data)
			.then((data) => new Blob([data], { type: 'application/csv' }))
			.then((blob) => URL.createObjectURL(blob))
			.then((uril) => {
				let link = document.createElement('a');
				link.setAttribute('href', uril);
				link.setAttribute('download', 'pedidos-b2b.csv');
				let clickEvent = new MouseEvent('click', {
					view: window,
					bubbles: true,
					cancelable: false,
				});
				link.dispatchEvent(clickEvent);
			})
			.catch(
				AxiosUtil.handleError.bind(
					AxiosUtil,
					'fail when trying to download ordersBtb in OrderBtbService.',
				),
			);
	}

	async getById(id) {
		return api
			.get(`${this.#prefix}/${id}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectOrder)
			.catch(
				AxiosUtil.handleError.bind(
					AxiosUtil,
					`fail when trying to get orderBtb with id ${id} in ${this.constructor.name}.`,
				),
			);
	}

	async create(orderForm) {
		return api
			.post(this.#prefix, orderForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectOrder)
			.catch(
				AxiosUtil.handleError.bind(
					AxiosUtil,
					'fail when trying to create orderBtb in OrderBtbService.',
				),
			);
	}

	async dispatch(ordersFrom) {
		return api
			.post(`${this.#prefix}/dispatch`, ordersFrom)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectOrders)
			.catch(
				AxiosUtil.handleError.bind(
					AxiosUtil,
					`fail when trying to dispatch ordersBtb in ${this.constructor.name}.`,
				),
			);
	}

	async finalize(id, orderBtbForm) {
		return api
			.post(`${this.#prefix}/${id}/finalize`, orderBtbForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectOrder)
			.catch(
				AxiosUtil.handleError.bind(
					AxiosUtil,
					`fail when trying to finalize orderBtb with id ${id} in ${this.constructor.name}.`,
				),
			);
	}

	async unfullfill(id, orderBtbForm) {
		return api
			.post(`${this.#prefix}/${id}/unfullfill`, orderBtbForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectOrder)
			.catch(
				AxiosUtil.handleError.bind(
					AxiosUtil,
					`fail when trying to unfullfill orderBtb with id ${id} in ${this.constructor.name}.`,
				),
			);
	}

	async hasOrderByTrackingCode(trackingCode) {
		return api
			.head(`${this.#prefix}/tracking-code/${trackingCode}`)
			.then((response) => true)
			.catch((error) => {
				if (error.response?.status === 404) {
					return false;
				}
				AxiosUtil.handleError(
					`fail when trying to check if has order by tracking code in ${this.constructor.name}.`,
					error,
				);
			});
	}

	async getOrderStatuses() {
		return api
			.get(`${this.#prefix}/statuses`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch(
				AxiosUtil.handleError.bind(
					AxiosUtil,
					`fail when trying to get order statuses in ${this.constructor.name}.`,
				),
			);
	}
}

const orderBtbService = new OrderBtbService();
export default orderBtbService;
